import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

// Google Analytics 4 configuration
const GA_MEASUREMENT_ID = 'G-9RXN5WCJQ4'; // Talal's actual GA4 measurement ID

// Initialize Google Analytics
const initGA = () => {
  // Only initialize in production
  if (process.env.NODE_ENV !== 'production') {
    console.log('Analytics disabled in development mode');
    return;
  }

  // Check if gtag is already loaded
  if (typeof window !== 'undefined' && !(window as any).gtag) {
    // Load Google Analytics script
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
    document.head.appendChild(script);

    // Initialize gtag
    (window as any).dataLayer = (window as any).dataLayer || [];
    (window as any).gtag = function() {
      (window as any).dataLayer.push(arguments);
    };

    (window as any).gtag('js', new Date());
    (window as any).gtag('config', GA_MEASUREMENT_ID, {
      // Privacy-friendly settings
      anonymize_ip: true,
      cookie_flags: 'SameSite=None;Secure',
      cookie_expires: 365 * 24 * 60 * 60, // 1 year
    });

    console.log('Google Analytics initialized');
  }
};

// Track page views
const trackPageView = (url: string, title?: string) => {
  if (process.env.NODE_ENV !== 'production') {
    console.log('Page view tracked (dev):', url, title);
    return;
  }

  if (typeof window !== 'undefined' && (window as any).gtag) {
    (window as any).gtag('config', GA_MEASUREMENT_ID, {
      page_path: url,
      page_title: title,
    });
  }
};

// Track custom events
export const trackEvent = (eventName: string, parameters?: Record<string, any>) => {
  if (process.env.NODE_ENV !== 'production') {
    console.log('Event tracked (dev):', eventName, parameters);
    return;
  }

  if (typeof window !== 'undefined' && (window as any).gtag) {
    (window as any).gtag('event', eventName, {
      event_category: 'engagement',
      event_label: parameters?.label,
      value: parameters?.value,
      ...parameters,
    });
  }
};

// Track user interactions
export const trackInteraction = (action: string, element: string) => {
  trackEvent('user_interaction', {
    action,
    element,
    timestamp: new Date().toISOString(),
  });
};

// Track performance metrics
export const trackPerformance = (metricName: string, value: number) => {
  trackEvent('performance_metric', {
    metric_name: metricName,
    metric_value: value,
  });
};

// Analytics component that tracks page views automatically
const Analytics: React.FC = () => {
  const location = useLocation();

  useEffect(() => {
    // Initialize analytics on first load
    initGA();
  }, []);

  useEffect(() => {
    // Track page view on route change
    trackPageView(location.pathname + location.search, document.title);
  }, [location]);

  // This component doesn't render anything
  return null;
};

export default Analytics; 